import React, { useContext, useState, useEffect, useRef } from 'react';
import { Navbar, Nav, NavDropdown, Container } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import './NavigationBar.css';
import logo from '../Components/4sightlogo.png';
import AuthContext from './AuthContext';

const API = process.env.REACT_APP_API_URL || '';
const authHeader = () => ({ Authorization: `Token ${localStorage.getItem("Token")}` });

const STATUS_COLORS = { online: '#22c55e', busy: '#f59e0b', offline: '#64748b', tea_break: '#d97706', lunch_break: '#f43f5e', see_you_tomorrow: '#818cf8', on_a_break: '#f97316' };
const STATUS_LABELS = { online: 'Online', busy: 'Busy', offline: 'Offline', tea_break: 'Tea Break', lunch_break: 'Lunch Break', see_you_tomorrow: 'Left for the Day', on_a_break: 'On a Break' };
const STATUS_EMOJI = { tea_break: '🍵', lunch_break: '🍱', see_you_tomorrow: '🌅', on_a_break: '☕' };

function StatusPicker({ isLoggedIn }) {
  const [myStatus, setMyStatus] = useState(() => localStorage.getItem('myStatus') || 'online');
  const [open, setOpen] = useState(false);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [breakContact, setBreakContact] = useState('');
  const [breakBackDate, setBreakBackDate] = useState('');
  const [breakBackTime, setBreakBackTime] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserDrop, setShowUserDrop] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => setMyStatus(e.detail || localStorage.getItem('myStatus') || 'online');
    window.addEventListener('myStatusChanged', h);
    return () => window.removeEventListener('myStatusChanged', h);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleOut = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleOut);
    return () => document.removeEventListener('mousedown', handleOut);
  }, [open]);

  const fetchUsers = async () => {
    if (allUsers.length) return;
    try {
      const res = await fetch(`${API}/users/names-departments/`, { headers: authHeader() });
      if (res.ok) { const d = await res.json(); setAllUsers(d); }
    } catch {}
  };

  const applyStatus = async (status) => {
    if (status === 'on_a_break') {
      setOpen(false);
      setBreakContact('');
      setBreakBackDate('');
      setBreakBackTime('');
      setUserSearch('');
      fetchUsers();
      setShowBreakModal(true);
      return;
    }
    setMyStatus(status);
    localStorage.setItem('myStatus', status);
    window.dispatchEvent(new CustomEvent('myStatusChanged', { detail: status }));
    setOpen(false);
    try {
      await fetch(`${API}/users/set-status/`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (status === 'see_you_tomorrow') {
        fetch(`${API}/api/dm/send-eod/`, {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
        }).catch(() => {});
      }
    } catch {}
  };

  const submitBreak = async () => {
    const status = 'on_a_break';
    setMyStatus(status);
    localStorage.setItem('myStatus', status);
    window.dispatchEvent(new CustomEvent('myStatusChanged', { detail: status }));
    setShowBreakModal(false);
    try {
      await fetch(`${API}/users/set-status/`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      fetch(`${API}/api/dm/send-break/`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_name: breakContact, back_at: breakBackDate && breakBackTime ? `${breakBackDate} at ${breakBackTime}` : breakBackDate || '' }),
      }).catch(() => {});
    } catch {}
  };

  if (!isLoggedIn) return null;

  const filteredUsers = allUsers.filter(u => (u.name || u.username || '').toLowerCase().includes(userSearch.toLowerCase())).slice(0, 8);

  return (
    <>
    <div ref={ref} style={{ position: 'relative', display: 'flex', alignItems: 'center', marginRight: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={`Status: ${STATUS_LABELS[myStatus] || STATUS_LABELS.offline}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 7,
          background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 20, padding: '5px 12px 5px 8px', cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
      >
        {STATUS_EMOJI[myStatus]
          ? <span style={{ fontSize: 14, lineHeight: 1 }}>{STATUS_EMOJI[myStatus]}</span>
          : <span style={{
              width: 11, height: 11, borderRadius: '50%',
              background: STATUS_COLORS[myStatus] || STATUS_COLORS.offline,
              display: 'block', flexShrink: 0,
              boxShadow: `0 0 6px ${STATUS_COLORS[myStatus] || STATUS_COLORS.offline}`,
            }} />}
        <span style={{ color: '#d0d6dc', fontSize: 13, fontWeight: 500 }}>{STATUS_LABELS[myStatus] || STATUS_LABELS.offline}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: '#1e293b', border: '1px solid #334155', borderRadius: 10,
          padding: '6px 0', boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 9999, minWidth: 160,
        }}>
          {['online', 'busy', 'offline', 'tea_break', 'lunch_break', 'on_a_break', 'see_you_tomorrow'].map(s => (
            <button
              key={s}
              onClick={() => applyStatus(s)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '9px 16px', border: 'none',
                background: myStatus === s ? 'rgba(13,148,136,0.2)' : 'transparent',
                color: '#e2e8f0', cursor: 'pointer', fontSize: 13, fontWeight: myStatus === s ? 700 : 400,
                textAlign: 'left',
              }}
            >
              {STATUS_EMOJI[s]
                ? <span style={{ fontSize: 14, lineHeight: 1, flexShrink: 0 }}>{STATUS_EMOJI[s]}</span>
                : <span style={{
                    width: 11, height: 11, borderRadius: '50%',
                    background: STATUS_COLORS[s], display: 'block', flexShrink: 0,
                    boxShadow: myStatus === s ? `0 0 6px ${STATUS_COLORS[s]}` : 'none',
                  }} />}
              {STATUS_LABELS[s]}
              {s === 'busy' && (
                <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 'auto' }}>mutes alerts</span>
              )}
              {s === 'on_a_break' && (
                <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 'auto' }}>sets contact</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>

    {/* On a Break modal */}
    {showBreakModal && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(100,116,139,0.35)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClick={() => setShowBreakModal(false)}>
        <div style={{ background: '#fff', borderRadius: 20, width: 400, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', overflow: 'hidden' }}
          onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '24px 24px 20px', position: 'relative' }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
                <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
                <line x1="6" y1="1" x2="6" y2="4"/>
                <line x1="10" y1="1" x2="10" y2="4"/>
                <line x1="14" y1="1" x2="14" y2="4"/>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15.5, fontWeight: 700, color: '#0f172a' }}>Going on a break</div>
              <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 3, lineHeight: 1.4 }}>Let your team know who to reach and when you'll be back</div>
            </div>
            <button onClick={() => setShowBreakModal(false)}
              style={{ position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 20, lineHeight: 1, padding: 4 }}>×</button>
          </div>

          {/* Body */}
          <div style={{ borderTop: '1px solid #f1f5f9', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* Contact person */}
            <div style={{ position: 'relative' }}>
              <label style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', letterSpacing: '0.07em', textTransform: 'uppercase', display: 'block', marginBottom: 7 }}>Who to reach in your absence</label>
              <input
                type="text"
                placeholder="Search team member…"
                value={userSearch}
                onChange={e => { setUserSearch(e.target.value); setBreakContact(e.target.value); setShowUserDrop(true); }}
                onFocus={() => setShowUserDrop(true)}
                onBlur={() => setTimeout(() => setShowUserDrop(false), 180)}
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                onFocusCapture={e => e.currentTarget.style.borderColor = '#f59e0b'}
                onBlurCapture={e => e.currentTarget.style.borderColor = '#e2e8f0'}
              />
              {showUserDrop && filteredUsers.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: 180, overflowY: 'auto', marginTop: 2 }}>
                  {filteredUsers.map((u, i) => (
                    <div key={i} onMouseDown={() => { setBreakContact(u.name || u.username); setUserSearch(u.name || u.username); setShowUserDrop(false); }}
                      style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fffbeb'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      {u.name || u.username}
                      {u.designation && <span style={{ color: '#94a3b8', marginLeft: 8, fontSize: 11 }}>{u.designation}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Back at */}
            <div>
              <label style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', letterSpacing: '0.07em', textTransform: 'uppercase', display: 'block', marginBottom: 7 }}>Back at</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="date" value={breakBackDate} onChange={e => setBreakBackDate(e.target.value)}
                  style={{ flex: 1, padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                  onFocus={e => e.currentTarget.style.borderColor = '#f59e0b'}
                  onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                />
                <input type="time" value={breakBackTime} onChange={e => setBreakBackTime(e.target.value)}
                  style={{ width: 110, padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                  onFocus={e => e.currentTarget.style.borderColor = '#f59e0b'}
                  onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px 20px' }}>
            <button onClick={() => setShowBreakModal(false)}
              style={{ padding: '10px 20px', border: 'none', borderRadius: 9, background: 'transparent', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: '#64748b', fontFamily: 'inherit' }}>
              Cancel
            </button>
            <button onClick={submitBreak}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 22px', border: 'none', borderRadius: 9, background: '#f59e0b', cursor: 'pointer', fontSize: 13.5, color: '#fff', fontWeight: 700, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
                <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
                <line x1="6" y1="1" x2="6" y2="4"/>
                <line x1="10" y1="1" x2="10" y2="4"/>
                <line x1="14" y1="1" x2="14" y2="4"/>
              </svg>
              Notify &amp; go on break
            </button>
          </div>

        </div>
      </div>
    )}
    </>
  );
}

const NavigationBar = () => {
  const { isLoggedIn, username, displayName, handleLogout } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    navigate(path);
  };

  const onLogoutClick = () => {
    handleLogout();
    navigate('/');
  };

  const dropdownTitle = isLoggedIn ? (
    <span className="nbr-username-display">Hi - {displayName || username}</span>
  ) : (
    <span className="nbr-login-text">Login</span>
  );

  return (
    <Navbar
      style={{ backgroundImage: 'linear-gradient(to right, var(--nbr-bg-start-color), var(--nbr-bg-end-color))' }}
      fixed="top"
      className="px-3 nbr-main-nav"
    >
      <Container fluid className="d-flex align-items-center justify-content-between">
        {/* Logo */}
        <Navbar.Brand as={Link} to="/" className="nbr-logo-link">
          <img src={logo} alt="Logo" className="nbr-logo-img" />
        </Navbar.Brand>

        {/* Right side: status picker + user dropdown */}
        <Nav className="ms-auto nbr-nav-links flex-row" style={{ alignItems: 'center' }}>
          <StatusPicker isLoggedIn={isLoggedIn} />
          <NavDropdown
            title={dropdownTitle}
            id="user-dropdown"
            className="nbr-custom-dropdown"
            menuVariant="dark"
            align="end"
            popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 8] } }] }}
            renderMenuOnMount={true}
          >
            {isLoggedIn ? (
              <>
                <NavDropdown.Item onClick={() => handleNavigation('/user-profile')}>Profile</NavDropdown.Item>
                <NavDropdown.Item onClick={() => handleNavigation('/settings')}>Settings</NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item onClick={onLogoutClick}>Logout</NavDropdown.Item>
              </>
            ) : (
              <NavDropdown.Item onClick={() => handleNavigation('/login')}>Login</NavDropdown.Item>
            )}
          </NavDropdown>
        </Nav>
      </Container>
    </Navbar>
  );
};

export default NavigationBar;
